import axios from 'axios';
import qs from 'qs';
import { useConfigHelper } from '@ilteducation/config';
import type {
  Annotation,
  CreateAnnotationRequest,
  CreateAnnotationResponse,
  GetAnnotationResponse,
  GetAnnotationsResponse,
  UpdateAnnotationRequest,
  UpdateAnnotationResponse,
} from '@ilteducation/annotations-types';

const { getApiUrl } = useConfigHelper();

// Giglets: We want to display both the students' and the teachers' annotations on the same page,
// but there is no way in the annotations service to share annotations with other users. Furthermore,
// Giglets users are not "ILT users" (yet) and a sharing feature would depend on the ILT user system.
// Thus, there is an API in Giglets to retrieve annotations for a student and their teachers.
// We need to call that API here, so we provide this override possibility.
// This is a workaround until we have a proper solution for sharing annotations.
const getApiUrlOverride = (apiName: string): string => {
  switch (window.location.hostname) {
    case 'pupil.localhost':
      return 'http://pupil.localhost:3030/api/annotations';
    case 'pupil-staging.gigletsedu.com':
      return 'https://pupil-staging.gigletsedu.com/api/annotations';
    case 'pupil.gigletsedu.com':
      return 'https://pupil.gigletsedu.com/api/annotations';
    case 'teacher.localhost':
      return 'http://teacher.localhost:3030/api/annotations';
    case 'teacher-staging.gigletsedu.com':
      return 'https://teacher-staging.gigletsedu.com/api/annotations';
    case 'teacher.gigletsedu.com':
      return 'https://teacher.gigletsedu.com/api/annotations';
    default:
      return getApiUrl(apiName);
  }
};

export const getAnnotations = async (
  contentId: string,
  type: string,
  profileId?: string,
): Promise<GetAnnotationsResponse> => {
  const urlQs = qs.stringify({ profileId });
  const params = urlQs ? `?${urlQs}` : '';

  const response = await axios.get(`${getApiUrlOverride('annotations')}/${contentId}/${type}${params}`);
  return response.data as GetAnnotationsResponse;
};

export const getAnnotation = async (annotationId: string, profileId?: string): Promise<Annotation | null> => {
  const urlQs = qs.stringify({ profileId });
  const params = urlQs ? `?${urlQs}` : '';
  const response = await axios.get(`${getApiUrl('annotations')}/${annotationId}${params}`);
  if (response.status === 404) {
    return null;
  }
  return response.data as GetAnnotationResponse;
};

export const create = async (payload: CreateAnnotationRequest, profileId?: string): Promise<Annotation> => {
  const urlQs = qs.stringify({ profileId });
  const params = urlQs ? `?${urlQs}` : '';
  const response = await axios.post(`${getApiUrl('annotations')}/${params}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data as CreateAnnotationResponse;
};

export const update = async (annotationId: string, payload: UpdateAnnotationRequest): Promise<Annotation> => {
  const response = await axios.patch(`${getApiUrl('annotations')}/${annotationId}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data as UpdateAnnotationResponse;
};

export const destroy = async (annotationId: string): Promise<void> => {
  await axios.delete(`${getApiUrl('annotations')}/${annotationId}`);
};
